import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import AllProjects from "../components/projects/allProjects";

import SEO from "../data/seo";
import { useTheme } from "../contexts/ThemeContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const ContentWrapper = styled.div<{ theme: Theme }>`
	max-width: ${props => props.theme.density.contentMaxWidth};
	margin: 0 auto;
	padding: 0 ${props => props.theme.density.pageGutter};

	@media (max-width: 768px) {
		padding: 0 ${props => props.theme.density.pageGutterMobile};
	}
`;

const ProjectsContainer = styled.div`
	padding-top: 1.5rem;
	animation: ${fadeInUp} 0.6s ease-out;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	color: ${props => props.theme.colors.text.primary};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	margin: 0 0 0.75rem;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const Subtitle = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.body.fontSize};
	line-height: ${props => props.theme.typography.roles.body.lineHeight};
	color: ${props => props.theme.colors.text.secondary};
	margin-bottom: 1.5rem;

	@media (max-width: 768px) {
		font-size: 1.1rem;
	}
`;

const ProjectsWrapper = styled.div`
	animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const FooterWrapper = styled.div`
	margin-top: 2.5rem;
`;

const Projects = () => {
	const { theme } = useTheme();
	const userInfo = useUserInfo();
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "projects")!;

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Projects | ${userInfo.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar active="projects" />
				<ContentWrapper theme={theme}>
					<ProjectsContainer>
						<Title theme={theme}>Projects</Title>
						<Subtitle theme={theme}>{userInfo.projects.tools.description}</Subtitle>
						<ProjectsWrapper>
							<AllProjects />
						</ProjectsWrapper>
					</ProjectsContainer>
					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</ContentWrapper>
			</PageContainer>
		</React.Fragment>
	);
};

export default Projects;
